import React, { createContext, useContext, useState, useEffect, useMemo, useCallback, useRef } from "react";
import ReactGA from "react-ga4"; // Import ReactGA for analytics
import { usePostHog } from "posthog-js/react"; // Import the PostHog React hook
import { useAuth } from "./AuthContext";
import { usePostHogTracking } from "../hooks/usePostHogTracking";

const DataContext = createContext();

export const isProductFamilyPayload = (data) => (
  Array.isArray(data) && data.some((item) => (
    Array.isArray(item?.direct_buckets) ||
    Array.isArray(item?.children) ||
    Array.isArray(item?.related_groups)
  ))
);

const getProductBucketKey = (bucket, fallback) =>
  bucket?.bucket_key || bucket?.canonical_key || bucket?.key || fallback;

const getProductFamilyKey = (family, fallback) =>
  family?.family_key || family?.key || fallback;

const productFamilyMetadata = (family) => ({
  family_key: getProductFamilyKey(family, null),
  family_slug: family?.family_slug,
  grouping_type: family?.grouping_type,
  grouping_kind: family?.grouping_kind,
  grouping_subtype: family?.grouping_subtype,
  membership: family?.membership,
  relationship_type: family?.relationship_type,
  relationship_kind: family?.relationship_kind,
  family_display_name: family?.family_display_name || family?.display_name || family?.title,
  family_brand: family?.family_brand || family?.brand,
  family_product_line: family?.family_product_line || family?.product_line,
  family_model: family?.family_model || family?.model,
  family_water_type: family?.family_water_type || family?.water_type,
});

export const flattenProductFamilyData = (data) => {
  if (!Array.isArray(data)) return [];
  if (!isProductFamilyPayload(data)) return data;

  const bucketsByKey = {};
  const visitFamily = (family, familyIndex, inheritedFamily = null) => {
    const familyKey = getProductFamilyKey(family, `family-${familyIndex}`);
    const familyMeta = productFamilyMetadata({ ...family, family_key: familyKey });
    const familyBuckets = [
      ...(family.direct_buckets || []),
      ...(family.context_buckets || []),
      ...(family.related_buckets || []),
    ];
    familyBuckets.forEach((bucket, bucketIndex) => {
      const bucketKey = getProductBucketKey(bucket, `${familyKey}-bucket-${bucketIndex}`);
      if (!bucketsByKey[bucketKey]) {
        bucketsByKey[bucketKey] = {
          bucket,
          families: [],
          familyKeys: new Set(),
          listingsByKey: {},
        };
      }

      const bucketRecord = bucketsByKey[bucketKey];
      [familyMeta, inheritedFamily].filter(Boolean).forEach((meta) => {
        if (meta.family_key && !bucketRecord.familyKeys.has(meta.family_key)) {
          bucketRecord.familyKeys.add(meta.family_key);
          bucketRecord.families.push(meta);
        }
      });

      (bucket.listings || []).forEach((listing, listingIndex) => {
        const listingKey = listing?.id !== undefined && listing?.id !== null
          ? `id:${listing.id}`
          : `${bucketKey}|${listing?.product_url || listing?.url || ""}|${listing?.variant_label || ""}|${listingIndex}`;
        if (!bucketRecord.listingsByKey[listingKey]) {
          bucketRecord.listingsByKey[listingKey] = listing;
        }
      });
    });
    (family.children || []).forEach((child, childIndex) => {
      visitFamily(child, `${familyIndex}-child-${childIndex}`, familyMeta);
    });
    (family.related_groups || []).forEach((related, relatedIndex) => {
      visitFamily(related, `${familyIndex}-related-${relatedIndex}`, familyMeta);
    });
  };
  data.forEach((family, familyIndex) => {
    visitFamily(family, familyIndex);
  });

  return Object.values(bucketsByKey).flatMap((bucketRecord) => {
    const bucket = bucketRecord.bucket || {};
    const bucketKey = getProductBucketKey(bucket, null);
    const primaryFamily = bucketRecord.families[0] || {};
    return Object.values(bucketRecord.listingsByKey).map((listing) => ({
      ...listing,
      bucket_key: bucketKey || listing?.bucket_key,
      display_name: bucket.display_name || listing?.display_name || listing?.title,
      family_key: primaryFamily.family_key || listing?.family_key,
      family_display_name: primaryFamily.family_display_name || listing?.family_display_name,
      families: bucketRecord.families,
      category_id: bucket.category_id ?? listing?.category_id,
      category: bucket.category ?? listing?.category,
      category_slug: bucket.category_slug ?? listing?.category_slug,
      brand: bucket.brand ?? listing?.brand,
      product_line: bucket.product_line ?? listing?.product_line,
      model: bucket.model ?? listing?.model,
      water_type: bucket.water_type ?? listing?.water_type,
      image_url: listing?.image_url || bucket.image_url || bucket.bucket_image_url,
      bucket_image_url: bucket.bucket_image_url || bucket.image_url || listing?.bucket_image_url,
      avg_rating: bucket.avg_rating ?? listing?.avg_rating,
      review_count: bucket.review_count ?? listing?.review_count ?? 0,
    }));
  });
};

export const useData = () => {
  const context = useContext(DataContext);
  if (!context) {
    throw new Error("useData must be used within a DataProvider");
  }
  return context;
};

export const DataProvider = ({ children }) => {
  // Get auth context
  const { isAuthenticated, token } = useAuth();
  
  // Get PostHog tracking functions
  const { trackFavoritesAction } = usePostHogTracking();
  
  const [fishData, setFishData] = useState([]);
  const [invertData, setInvertData] = useState([]);
  const [plantData, setPlantData] = useState([]);
  const [categoryData, setCategoryData] = useState([]);
  const [reviewsData, setReviewsData] = useState([]);
  const [storesData, setStoresData] = useState({});
  const [topListingsData, setTopListingsData] = useState([]);
  const [historicalPriceData, setHistoricalPriceData] = useState([]);
  const [productData, setProductData] = useState([]);
  const [productFamilyData, setProductFamilyData] = useState([]);
  const [productCategoryData, setProductCategoryData] = useState([]);
  const [productHistoricalPriceData, setProductHistoricalPriceData] = useState([]);

  // Classifieds state
  const [classifieds, setClassifieds] = useState({});

  // Favorites state
  const [userFavorites, setUserFavorites] = useState([]);
  const [userSubscriptions, setUserSubscriptions] = useState([]);
  
  // Care guides state
  const [careGuidesData, setCareGuidesData] = useState([]);
  const [careGuideDetails, setCareGuideDetails] = useState({});

  // Reefcyclopedia state
  const [reefcyclopediaData, setReefcyclopediaData] = useState([]);

  // Price digest state
  const [fishDigest, setFishDigest] = useState(null);
  const [invertDigest, setInvertDigest] = useState(null);
  const [plantDigest, setPlantDigest] = useState(null);
  const [productDigest, setProductDigest] = useState(null);

  // Reef Wizard recommendations state
  const [reefWizardRecommendations, setReefWizardRecommendations] = useState([]);
  const [reefWizardTanks, setReefWizardTanks] = useState([]);

  // Shopping lists state
  const [shoppingLists, setShoppingLists] = useState([]);
  const shoppingListsRequestId = useRef(0);

  // Unified error states for each data type
  const [errors, setErrors] = useState({
    fish: null,
    inverts: null,
    plants: null,
    categories: null,
    stores: null,
    topListings: null,
    historicalPrices: null,
    products: null,
    productCategories: null,
    productHistoricalPrices: null,
    reviews: null,
    classifieds: null,
    favorites: null,
    subscriptions: null,
    careGuides: null,
    reefcyclopedia: null,
    reefWizardRecommendations: null,
    reefWizardTanks: null,
    shoppingLists: null,
  });

  // API endpoints storage
  const [apiEndpoints, setApiEndpoints] = useState({});
  const [apiEndpointsLoaded, setApiEndpointsLoaded] = useState(false);
  const [loadingApiIndex, setApiIndexLoading] = useState(true);
  const [apiError, setApiError] = useState(null);

  // Loading states for each data type
  const [loading, setLoading] = useState({
    fish: false,
    inverts: false,
    plants: false,
    categories: false,
    stores: false,
    topListings: false,
    historicalPrices: false,
    products: false,
    productCategories: false,
    productHistoricalPrices: false,
    reviews: false,
    classifieds: false,
    favorites: false,
    subscriptions: false,
    careGuides: false,
    reefcyclopedia: false,
    reefWizardRecommendations: false,
    reefWizardTanks: false,
    shoppingLists: false,
  });

  // Get PostHog instance from the React hook
  const posthog = usePostHog();

  // Data loading status
  const [dataLoaded, setDataLoaded] = useState({
    fish: false,
    inverts: false,
    plants: false,
    categories: false,
    stores: false,
    topListings: false,
    historicalPrices: false,
    products: false,
    productCategories: false,
    productHistoricalPrices: false,
    reviews: false,
    classifieds: false,
    favorites: false,
    subscriptions: false,
    careGuides: false,
    reefcyclopedia: false,
    reefWizardRecommendations: false,
    reefWizardTanks: false,
    shoppingLists: false,
  });

  // Simple boolean maps for image loading status
  // Maps scientific_name -> true if cached image failed to load
  const [cachedImageFailed, setCachedImageFailed] = useState({});
  // Maps scientific_name -> true if image loaded successfully from either source
  const [imageLoaded, setImageLoaded] = useState({});

  const markDataAsLoaded = (dataType) => {
    if (dataLoaded[dataType]) {
      return;
    }

    setDataLoaded((prev) => ({
      ...prev,
      [dataType]: true,
    }));
  };

  // Function to mark a cached image as failed
  const markCachedImageFailed = (scientificName) => {
    if (cachedImageFailed[scientificName]) {
      return;
    }

    setCachedImageFailed((prev) => ({
      ...prev,
      [scientificName]: true,
    }));
  };

  // Function to mark an image as successfully loaded with analytics tracking
  const markImageLoaded = (scientificName) => {
    if (imageLoaded[scientificName]) {
      return;
    }

    // Track image load success in Google Analytics
    ReactGA.event({
      category: "Image",
      action: "Image Load Success",
      label: scientificName,
    });

    // Track in PostHog using the React integration
    if (posthog) {
      // This generated 250k+ events/month, so disabled for now
      /*
      posthog.capture("image_loaded", {
        scientific_name: scientificName,
        cached: !cachedImageFailed[scientificName],
      });
      */
    }

    setImageLoaded((prev) => ({
      ...prev,
      [scientificName]: true,
    }));
  };

  // API utilities
  const IS_DEBUG = window._env_?.REACT_IS_DEBUG === "true";
  const BACKEND_URL = window._env_?.REACT_APP_BACKEND_URL;
  const API_INDEX_URL = `${BACKEND_URL}/index`;

  // Get API URL from endpoints
  const getApiUrl = (endpoint, param = null) => {
    if (!apiEndpoints[endpoint]) {
      return null;
    }
    return param
      ? `${apiEndpoints[endpoint]}/${param}`
      : apiEndpoints[endpoint];
  };

  // Load API index
  const loadApiIndex = async () => {
    if (apiEndpointsLoaded) {
      setApiIndexLoading(false);
      return;
    }

    try {
      const response = await fetch(API_INDEX_URL, {
        headers: {
          "Content-Type": "application/json",
          "Access-Control-Allow-Origin": "*",
        },
      });
      if (!response.ok) {
        throw new Error("Failed to fetch API index");
      }
      const data = await response.json();
      setApiEndpoints(data);
      setApiEndpointsLoaded(true);
      setApiError(null);
    } catch (error) {
      console.error("Error fetching API index:", error);
      setApiError(
        "Failed to load API endpoints. Please try refreshing the page."
      );
    } finally {
      setApiIndexLoading(false);
    }
  };

  // Generic API data loader
  const loadApiData = async (endpoint, setData, setLoading, dataType, authToken = null, isCurrent = () => true) => {
    // Wait for API index to load
    if (loadingApiIndex) {
      return;
    }

    if (!apiEndpoints[endpoint]) {
      setApiError(`API endpoint ${endpoint} not found`);
      setLoading(false);
      return;
    }

    try {
      const url = getApiUrl(endpoint);
      const headers = {
        "Content-Type": "application/json",
        "Access-Control-Allow-Origin": "*",
      };
      
      if (authToken) {
        headers["Authorization"] = `Bearer ${authToken}`;
      }
      
      const response = await fetch(url, { headers });
      
      if (!isCurrent()) return;

      if (response.status === 403) {
        // Handle authentication failure - clear data and mark as loaded
        setData([]);
        if (dataType) {
          markDataAsLoaded(dataType);
        }
        setLoading(false);
        return;
      }
      
      if (!response.ok) {
        throw new Error("Network response was not ok");
      }
      if (response.status === 204) {
        setTimeout(
          () => loadApiData(endpoint, setData, setLoading, dataType, authToken),
          1000
        );
        return;
      }
      const data = await response.json();
      if (!isCurrent()) return;
      setData(data);
      if (dataType) {
        markDataAsLoaded(dataType);
      }
    } catch (error) {
      if (isCurrent()) {
        console.error(`Error loading ${endpoint}:`, error);
        setApiError(error.message);
      }
    } finally {
      if (isCurrent()) setLoading(false);
    }
  };

  // Individual fetch methods for each data type
  const fetchFishData = async () => {
    if (loading.fish) return;
    setLoading((prev) => ({ ...prev, fish: true }));
    loadApiData(
      "listings",
      setFishData,
      (isLoading) => setLoading((prev) => ({ ...prev, fish: isLoading })),
      "fish"
    );
  };

  const fetchInvertData = async () => {
    if (loading.inverts) return;
    setLoading((prev) => ({ ...prev, inverts: true }));
    loadApiData(
      "invertebrate_listings",
      setInvertData,
      (isLoading) => setLoading((prev) => ({ ...prev, inverts: isLoading })),
      "inverts"
    );
  };

  const fetchPlantData = async () => {
    if (loading.plants) return;
    setLoading((prev) => ({ ...prev, plants: true }));
    loadApiData(
      "plant_listings",
      setPlantData,
      (isLoading) => setLoading((prev) => ({ ...prev, plants: isLoading })),
      "plants"
    );
  };

  const fetchCategoryData = async () => {
    if (loading.categories) return;
    setLoading((prev) => ({ ...prev, categories: true }));
    loadApiData(
      "categories",
      setCategoryData,
      (isLoading) => setLoading((prev) => ({ ...prev, categories: isLoading })),
      "categories"
    );
  };

  const fetchStoresData = async () => {
    if (loading.stores) return;
    setLoading((prev) => ({ ...prev, stores: true }));
    loadApiData(
      "stores",
      setStoresData,
      (isLoading) => setLoading((prev) => ({ ...prev, stores: isLoading })),
      "stores"
    );
  };

  const fetchTopListingsData = async () => {
    if (loading.topListings) return;
    setLoading((prev) => ({ ...prev, topListings: true }));
    loadApiData(
      "top_listings",
      setTopListingsData,
      (isLoading) =>
        setLoading((prev) => ({ ...prev, topListings: isLoading })),
      "topListings"
    );
  };

  const fetchHistoricalPriceData = async () => {
    if (loading.historicalPrices) return;
    setLoading((prev) => ({ ...prev, historicalPrices: true }));
    loadApiData(
      "historical_prices",
      setHistoricalPriceData,
      (isLoading) =>
        setLoading((prev) => ({ ...prev, historicalPrices: isLoading })),
      "historicalPrices"
    );
  };

  const fetchProductData = async () => {
    if (loading.products) return;
    setLoading((prev) => ({ ...prev, products: true }));
    const setProductPayloadData = (data) => {
      const rows = Array.isArray(data) ? data : [];
      setProductFamilyData(isProductFamilyPayload(rows) ? rows : []);
      setProductData(flattenProductFamilyData(rows));
    };
    loadApiData(
      "product_listings",
      setProductPayloadData,
      (isLoading) => setLoading((prev) => ({ ...prev, products: isLoading })),
      "products"
    );
  };

  const fetchProductCategoryData = async () => {
    if (loading.productCategories) return;
    setLoading((prev) => ({ ...prev, productCategories: true }));
    loadApiData(
      "product_categories",
      setProductCategoryData,
      (isLoading) => setLoading((prev) => ({ ...prev, productCategories: isLoading })),
      "productCategories"
    );
  };

  const fetchProductHistoricalPriceData = async () => {
    if (loading.productHistoricalPrices) return;
    setLoading((prev) => ({ ...prev, productHistoricalPrices: true }));
    loadApiData(
      "product_historical_prices",
      setProductHistoricalPriceData,
      (isLoading) => setLoading((prev) => ({ ...prev, productHistoricalPrices: isLoading })),
      "productHistoricalPrices"
    );
  };

  const fetchReviewsData = async () => {
    if (loading.reviews) return;
    setLoading((prev) => ({ ...prev, reviews: true }));
    loadApiData(
      "reviews",
      setReviewsData,
      (isLoading) => setLoading((prev) => ({ ...prev, reviews: isLoading })),
      "reviews"
    );
  };

  const fetchClassifieds = async () => {
    if (loading.classifieds) return;
    setLoading((prev) => ({ ...prev, classifieds: true }));
    loadApiData(
      "classifieds",
      setClassifieds,
      (isLoading) => setLoading((prev) => ({ ...prev, classifieds: isLoading })),
      "classifieds"
    );
  };

  const fetchCareGuides = async () => {
    if (loading.careGuides) return;
    setLoading((prev) => ({ ...prev, careGuides: true }));

    try {
      const url = apiEndpoints.care_guides || `${BACKEND_URL}/care-guides`;
      const response = await fetch(url, {
        headers: { "Content-Type": "application/json" },
      });
      if (!response.ok) throw new Error("Failed to fetch care guides");
      const data = await response.json();
      setCareGuidesData(data.guides || []);
      markDataAsLoaded("careGuides");
    } catch (error) {
      console.error("Error fetching care guides:", error);
      setErrors((prev) => ({ ...prev, careGuides: error }));
    }

    setLoading((prev) => ({ ...prev, careGuides: false }));
  };

  const fetchCareGuideByScientificName = async (scientificName) => {
    if (careGuideDetails[scientificName] !== undefined) {
      return careGuideDetails[scientificName];
    }

    try {
      const url = `${BACKEND_URL}/care-guides/${encodeURIComponent(scientificName)}`;
      const response = await fetch(url, {
        headers: { "Content-Type": "application/json" },
      });
      const guide = response.ok ? await response.json() : null;
      setCareGuideDetails((prev) => ({ ...prev, [scientificName]: guide }));
      return guide;
    } catch (error) {
      console.error(`Error fetching care guide for ${scientificName}:`, error);
      setCareGuideDetails((prev) => ({ ...prev, [scientificName]: null }));
      return null;
    }
  };

  const fetchReefcyclopedia = async () => {
    if (loading.reefcyclopedia) return;
    setLoading((prev) => ({ ...prev, reefcyclopedia: true }));

    try {
      const url = apiEndpoints.reefcyclopedia || `${BACKEND_URL}/reefcyclopedia`;
      const response = await fetch(url, {
        headers: { "Content-Type": "application/json" },
      });
      if (!response.ok) throw new Error("Failed to fetch reefcyclopedia articles");
      const data = await response.json();
      setReefcyclopediaData(data.articles || []);
      markDataAsLoaded("reefcyclopedia");
    } catch (error) {
      console.error("Error fetching reefcyclopedia:", error);
      setErrors((prev) => ({ ...prev, reefcyclopedia: error }));
    }

    setLoading((prev) => ({ ...prev, reefcyclopedia: false }));
  };

  const fetchPriceDigest = async (listingType) => {
    const digestKeys = {
      fish: "price_digest_fish",
      inverts: "price_digest_inverts",
      plants: "price_digest_plants",
      products: "price_digest_products",
    };
    const key = digestKeys[listingType] || digestKeys.fish;
    try {
      const url = apiEndpoints[key] || `${BACKEND_URL}/price-digest/${listingType}`;
      const response = await fetch(url, {
        headers: { "Content-Type": "application/json" },
      });
      if (!response.ok) return;
      const data = await response.json();
      if (listingType === "fish") {
        setFishDigest(data);
      } else if (listingType === "inverts") {
        setInvertDigest(data);
      } else if (listingType === "plants") {
        setPlantDigest(data);
      } else if (listingType === "products") {
        setProductDigest(data);
      }
    } catch (error) {
      console.error(`Error fetching ${listingType} price digest:`, error);
    }
  };

  const fetchUserFavorites = async (authToken) => {
    if (loading.favorites) return;
    setLoading((prev) => ({ ...prev, favorites: true }));
    loadApiData(
      "favorites",
      setUserFavorites,
      (isLoading) => setLoading((prev) => ({ ...prev, favorites: isLoading })),
      "favorites",
      authToken
    );
  };

  const fetchUserSubscriptions = async (authToken) => {
    if (loading.subscriptions) return;
    setLoading((prev) => ({ ...prev, subscriptions: true }));
    loadApiData(
      "subscriptions",
      setUserSubscriptions,
      (isLoading) => setLoading((prev) => ({ ...prev, subscriptions: isLoading })),
      "subscriptions",
      authToken
    );
  };

  const fetchShoppingLists = async (authToken) => {
    const requestId = ++shoppingListsRequestId.current;
    setLoading((prev) => ({ ...prev, shoppingLists: true }));
    return loadApiData(
      "shopping_lists",
      setShoppingLists,
      (isLoading) => setLoading((prev) => ({ ...prev, shoppingLists: isLoading })),
      "shoppingLists",
      authToken,
      () => requestId === shoppingListsRequestId.current
    );
  };

  // Fetch Reef Wizard recommended species
  const fetchReefWizardRecommendations = async (authToken, tankId = null) => {
    if (loading.reefWizardRecommendations) return;
    setLoading((prev) => ({ ...prev, reefWizardRecommendations: true }));
    
    try {
      let url = `${BACKEND_URL}/reef-wizard/recommended-species`;
      if (tankId) {
        url += `?tank_id=${tankId}`;
      }
      
      const response = await fetch(url, {
        headers: {
          Authorization: `Bearer ${authToken}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setReefWizardRecommendations(data.species || []);
        markDataAsLoaded("reefWizardRecommendations");
      }
    } catch (error) {
      console.error("Error fetching reef wizard recommendations:", error);
      setErrors((prev) => ({ ...prev, reefWizardRecommendations: error }));
    }
    
    setLoading((prev) => ({ ...prev, reefWizardRecommendations: false }));
  };

  // Fetch Reef Wizard tanks
  const fetchReefWizardTanks = async (authToken) => {
    if (loading.reefWizardTanks) return;
    setLoading((prev) => ({ ...prev, reefWizardTanks: true }));
    
    try {
      const response = await fetch(`${BACKEND_URL}/reef-wizard/tanks`, {
        headers: {
          Authorization: `Bearer ${authToken}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setReefWizardTanks(data.tanks || []);
        markDataAsLoaded("reefWizardTanks");
      }
    } catch (error) {
      console.error("Error fetching reef wizard tanks:", error);
      setErrors((prev) => ({ ...prev, reefWizardTanks: error }));
    }
    
    setLoading((prev) => ({ ...prev, reefWizardTanks: false }));
  };

  const favoriteTypeConfigs = {
    species: {
      payloadField: "scientific_name",
      responsePath: ["species", "scientific_name"],
      analyticsField: "scientific_name",
      defaultSource: "fish_listing",
    },
    product: {
      payloadField: "product_bucket_key",
      responsePath: ["product_bucket", "canonical_key"],
      analyticsField: "product_bucket_key",
      defaultSource: "supplies_listing",
    },
  };

  const getFavoriteTypeConfig = (listingType = "species") =>
    favoriteTypeConfigs[listingType] || favoriteTypeConfigs.species;

  const readFavoritePath = (favorite, path) =>
    path.reduce((value, field) => value?.[field], favorite);

  const buildFavoritePayload = (favoriteKey, listingType) => {
    const config = getFavoriteTypeConfig(listingType);
    return {
      listing_type: listingType,
      [config.payloadField]: favoriteKey,
    };
  };

  const buildFavoriteTrackingProperties = (favoriteKey, listingType, source) => {
    const config = getFavoriteTypeConfig(listingType);
    return {
      [config.analyticsField]: favoriteKey,
      listing_type: listingType,
      source: source || config.defaultSource,
    };
  };

  const favoriteMatches = (favorite, favoriteKey, listingType = "species") => {
    const config = getFavoriteTypeConfig(listingType);
    return readFavoritePath(favorite, config.responsePath) === favoriteKey;
  };

  // Add a favorite
  const addFavorite = async (scientificName, authToken, options = {}) => {
    const listingType = options.listingType || "species";
    try {
      const response = await fetch(getApiUrl("favorites"), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${authToken}`,
        },
        body: JSON.stringify(buildFavoritePayload(scientificName, listingType)),
      });

      if (response.ok) {
        // Get the complete favorite object from the response
        const newFavorite = await response.json();
        
        // Update local state immediately with the complete favorite object
        setUserFavorites((prev) => {
          const exists = prev.some((favorite) =>
            favoriteMatches(favorite, scientificName, listingType)
          );
          if (!exists) {
            return [...prev, newFavorite];
          }
          return prev;
        });

        // Track favorite addition with PostHog
        trackFavoritesAction(
          "added",
          buildFavoriteTrackingProperties(scientificName, listingType, options.source)
        );

        return true;
      } else {
        console.error("Failed to add favorite");
        return false;
      }
    } catch (error) {
      console.error("Error adding favorite:", error);
      return false;
    }
  };

  // Remove a favorite
  const removeFavorite = async (scientificName, authToken, options = {}) => {
    const listingType = options.listingType || "species";
    try {
      const response = await fetch(
        `${getApiUrl("favorites")}/${encodeURIComponent(scientificName)}?listing_type=${encodeURIComponent(listingType)}`,
        {
          method: "DELETE",
          headers: {
            "Authorization": `Bearer ${authToken}`,
          },
        }
      );

      if (response.ok) {
        // Update local state immediately by filtering out the removed favorite
        setUserFavorites((prev) => 
          prev.filter((favorite) =>
            !favoriteMatches(favorite, scientificName, listingType)
          )
        );

        // Track favorite removal with PostHog
        trackFavoritesAction(
          "removed",
          buildFavoriteTrackingProperties(scientificName, listingType, options.source)
        );

        return true;
      } else {
        console.error("Failed to remove favorite");
        return false;
      }
    } catch (error) {
      console.error("Error removing favorite:", error);
      return false;
    }
  };

  // Check if a species or product bucket is favorited
  const isFavorited = (favorites, favoriteKey, options = {}) => {
    const listingType = options.listingType || "species";
    return (favorites || []).some((favorite) =>
      favoriteMatches(favorite, favoriteKey, listingType)
    );
  };

  // Get subscriptions for a specific species
  const getSpeciesSubscriptions = (subscriptions, scientificName) => {
    return subscriptions.filter(sub =>
      sub.listing_type !== "product" && sub.scientific_name === scientificName
    );
  };

  // Check if a species has active subscriptions
  const hasActiveSubscriptions = (subscriptions, scientificName) => {
    return subscriptions.some(sub => 
      sub.listing_type !== "product" &&
      sub.scientific_name === scientificName && sub.is_active
    );
  };

  // Search function
  const searchSpecies = async (query, signal, includeFish = true, includeInvertebrates = true, includePlants = true) => {
    if (!query.trim() || !apiEndpoints.search_rank) {
      return {};
    }

    try {
      const params = new URLSearchParams({
        query: query,
        include_fish: includeFish.toString(),
        include_invertebrates: includeInvertebrates.toString(),
        include_plants: includePlants.toString()
      });
      
      const response = await fetch(
        `${apiEndpoints.search_rank}?${params.toString()}`,
        {
          headers: {
            "Content-Type": "application/json",
            "Access-Control-Allow-Origin": "*",
          },
          signal: signal,
        }
      );
      if (!response.ok) {
        throw new Error("Failed to fetch search results");
      }
      return await response.json();
    } catch (error) {
      // Only log errors that aren't from aborted requests
      if (error.name !== 'AbortError') {
        console.error("Error fetching search results:", error);
      }
      return null;
    }
  };

  const searchProducts = async (query, signal) => {
    if (!query.trim()) {
      return {};
    }

    try {
      const url = apiEndpoints.product_search_rank || `${BACKEND_URL}/products/search/rank`;
      const params = new URLSearchParams({ query });
      const response = await fetch(
        `${url}?${params.toString()}`,
        {
          headers: {
            "Content-Type": "application/json",
            "Access-Control-Allow-Origin": "*",
          },
          signal,
        }
      );
      if (!response.ok) {
        throw new Error("Failed to fetch product search results");
      }
      return await response.json();
    } catch (error) {
      if (error.name !== "AbortError") {
        console.error("Error fetching product search results:", error);
      }
      return null;
    }
  };

  // Initialize API index on mount
  useEffect(() => {
    loadApiIndex();
  }, []);

  // Auto-load all data once API endpoints are available
  useEffect(() => {
    if (apiEndpointsLoaded && !loadingApiIndex) {
      fetchFishData();
      fetchInvertData();
      fetchPlantData();
      fetchCategoryData();
      fetchStoresData();
      fetchTopListingsData();
      fetchHistoricalPriceData();
      fetchProductData();
      fetchProductCategoryData();
      fetchProductHistoricalPriceData();
      fetchReviewsData();
      fetchClassifieds();
      fetchCareGuides();
      fetchReefcyclopedia();
      fetchPriceDigest("fish");
      fetchPriceDigest("inverts");
      fetchPriceDigest("plants");
      fetchPriceDigest("products");
    }
  }, [apiEndpointsLoaded, loadingApiIndex]);

  // Load user-specific data when authentication changes
  useEffect(() => {
    if (apiEndpointsLoaded && !loadingApiIndex) {
      if (isAuthenticated && token) {
        fetchUserFavorites(token);
        fetchUserSubscriptions(token);
        fetchShoppingLists(token);
      } else {
        // Clear user data when not authenticated
        shoppingListsRequestId.current += 1;
        setUserFavorites([]);
        setUserSubscriptions([]);
        setShoppingLists([]);
        setReefWizardRecommendations([]);
        setReefWizardTanks([]);
        setDataLoaded((prev) => ({ 
          ...prev, 
          favorites: false, 
          subscriptions: false,
          shoppingLists: false,
          reefWizardRecommendations: false,
          reefWizardTanks: false,
        }));
      }
    }
  }, [isAuthenticated, token, apiEndpointsLoaded, loadingApiIndex]);

  const invalidateUserContent = () => {
    setDataLoaded((prev) => ({
      ...prev,
      reviews: false,
      classifieds: false,
    }));
    setLoading((prev) => ({
      ...prev,
      reviews: false,
      classifieds: false,
    }));
    fetchReviewsData();
    fetchClassifieds();
  };

  const retryAll = useCallback(() => {
    setApiError(null);
    if (!apiEndpointsLoaded) {
      loadApiIndex();
    } else {
      if (!dataLoaded.fish) fetchFishData();
      if (!dataLoaded.inverts) fetchInvertData();
      if (!dataLoaded.plants) fetchPlantData();
      if (!dataLoaded.categories) fetchCategoryData();
      if (!dataLoaded.stores) fetchStoresData();
      if (!dataLoaded.topListings) fetchTopListingsData();
      if (!dataLoaded.historicalPrices) fetchHistoricalPriceData();
      if (!dataLoaded.products) fetchProductData();
      if (!dataLoaded.productCategories) fetchProductCategoryData();
      if (!dataLoaded.productHistoricalPrices) fetchProductHistoricalPriceData();
      if (!dataLoaded.reviews) fetchReviewsData();
      if (!dataLoaded.classifieds) fetchClassifieds();
      if (!dataLoaded.careGuides) fetchCareGuides();
      if (!dataLoaded.reefcyclopedia) fetchReefcyclopedia();
    }
  }, [apiEndpointsLoaded, dataLoaded, fetchCareGuides, fetchCategoryData, fetchClassifieds, fetchFishData, fetchHistoricalPriceData, fetchInvertData, fetchPlantData, fetchProductCategoryData, fetchProductData, fetchProductHistoricalPriceData, fetchReefcyclopedia, fetchReviewsData, fetchStoresData, fetchTopListingsData, loadApiIndex]);

  const careGuidesMap = useMemo(() => {
    const map = {};
    careGuidesData.forEach((guide) => {
      if (guide.scientific_name) {
        map[guide.scientific_name.toLowerCase()] = guide.summary || {};
      }
    });
    return map;
  }, [careGuidesData]);

  const value = {
    fishData,
    invertData,
    plantData,
    categoryData,
    storesData,
    topListingsData,
    historicalPriceData,
    productData,
    productFamilyData,
    productCategoryData,
    productHistoricalPriceData,
    reviewsData,
    classifieds,
    userFavorites,
    userSubscriptions,
    careGuidesData,
    careGuidesMap,
    careGuideDetails,
    reefcyclopediaData,
    fishDigest,
    invertDigest,
    plantDigest,
    productDigest,
    reefWizardRecommendations,
    reefWizardTanks,
    shoppingLists,
    apiEndpoints,
    apiEndpointsLoaded,
    dataLoaded,
    loading,
    errors,

    // Individual fetch methods
    fetchFishData,
    fetchInvertData,
    fetchPlantData,
    fetchCategoryData,
    fetchStoresData,
    fetchTopListingsData,
    fetchHistoricalPriceData,
    fetchProductData,
    fetchProductCategoryData,
    fetchProductHistoricalPriceData,
    fetchReviewsData,
    fetchClassifieds,
    fetchCareGuides,
    fetchCareGuideByScientificName,
    fetchReefcyclopedia,
    fetchPriceDigest,
    fetchUserFavorites,
    fetchUserSubscriptions,
    fetchReefWizardRecommendations,
    fetchReefWizardTanks,
    fetchShoppingLists,
    invalidateUserContent,
    retryAll,

    // Favorites and subscriptions management
    addFavorite,
    removeFavorite,
    isFavorited,
    getSpeciesSubscriptions,
    hasActiveSubscriptions,

    // Expose the new simplified image state
    cachedImageFailed,
    markCachedImageFailed,
    imageLoaded,
    markImageLoaded,

    // API utilities
    loadingApiIndex,
    apiError,
    getApiUrl,
    loadApiIndex,
    loadApiData,
    searchSpecies,
    searchProducts,
    BACKEND_URL,
  };

  return <DataContext.Provider value={value}>{children}</DataContext.Provider>;
};

export default DataContext;
